import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ProjectsView from './projects/ProjectsView';
import CategoriesView from './projects/CategoriesView';
import MyTasksView from './projects/MyTasksView';
import SingleProjectView from './projects/SingleProjectView';
import './Projects.scss';

const Projects = ({ page, ...props }) => {

    const isAdmin = props.isAdmin;
    const userId = props.userId;

    const jwtToken = useSelector((state) => state.auth.jwtToken);
    
    
    // Helper function to get view from URL
    const getViewFromUrl = () => {
        const params = new URLSearchParams(window.location.search);
        const viewParam = params.get('view');
        const projectIdParam = params.get('project');
        
        // If project ID is in URL, show single project view
        if (projectIdParam && viewParam !== 'my-tasks') {
            return { view: 'single-project', projectId: projectIdParam };
        }
        
        // Map URL params to menu items
        if (viewParam === 'categories') return { view: 'categories' };
        if (viewParam === 'my-tasks' || viewParam === 'my tasks') return { view: 'my tasks' };
        return { view: 'projects' }; // default
    };

    // Helper function to update URL with view parameter
    const updateUrlWithView = (view) => {
        const basePath = window.location.pathname;
        let viewParam = '';
        if (view === 'categories') {
            viewParam = 'categories';
        } else if (view === 'my tasks') {
            viewParam = 'my-tasks';
        }
        // If it's 'projects', remove the view param or use clean URL
        const newUrl = viewParam 
            ? `${basePath}?view=${viewParam}`
            : basePath.split('?')[0];
        window.history.pushState({ view }, '', newUrl);
    };

    const [currentView, setCurrentView] = useState(() => getViewFromUrl());
    let menuItems = ['projects', 'categories', 'my tasks'];

    if (!isAdmin) {
        menuItems = menuItems.filter(item => item !== 'categories');
    }

    // Handle URL changes on mount and browser navigation
    useEffect(() => {
        const viewFromUrl = getViewFromUrl();
        if (viewFromUrl.view !== currentView.view || viewFromUrl.projectId !== currentView.projectId) {
            setCurrentView(viewFromUrl);
        }

        // Handle browser back/forward navigation
        const handlePopState = (event) => {
            const view = event.state?.view ? { view: event.state.view, projectId: event.state.projectId } : getViewFromUrl();
            setCurrentView(view);
        };

        window.addEventListener('popstate', handlePopState);
        
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [currentView]);

    // Update menu and URL when menu item is clicked
    const handleMenuClick = (menuItem) => {
        setCurrentView({ view: menuItem });
        updateUrlWithView(menuItem);
    };

    // Handle navigate to single project
    const handleViewProject = (projectId) => {
        const basePath = window.location.pathname;
        const newUrl = `${basePath}?project=${projectId}`;
        window.history.pushState({ view: 'single-project', projectId }, '', newUrl);
        setCurrentView({ view: 'single-project', projectId });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Handle back from single project
    const handleBackFromProject = () => {
        const basePath = window.location.pathname;
        window.history.pushState({ view: 'projects' }, '', basePath);
        setCurrentView({ view: 'projects' });
    };
    

    if (!jwtToken) {
        return (
            <div className="octopus-messages-container">
                <div className="octopus-not-logged-in">
                    <p>Please log in to view projects.</p>
                </div>
            </div>
        );
    }

    // Show single project view
    if (currentView.view === 'single-project' && currentView.projectId) {
        return (
            <div className="octopus-projects-container">
                <SingleProjectView 
                    projectId={parseInt(currentView.projectId)} 
                    onBack={handleBackFromProject}
                    currentUserId={userId}
                />
            </div>
        );
    }

    return (
        <div className="octopus-projects-container">
            {/* Top Navigation Menu */}
            <div className="octopus-projects-top-nav">
                <div className="octopus-projects-nav-tabs">
                    {menuItems.map((item) => (
                        <button
                            key={item}
                            className={`octopus-projects-nav-tab ${currentView.view === item ? 'active' : ''}`}
                            onClick={() => handleMenuClick(item)}
                        >
                            {item.charAt(0).toUpperCase() + item.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content based on active menu */}
            {currentView.view === 'projects' && <ProjectsView onViewProject={handleViewProject} />}
            {currentView.view === 'categories' && isAdmin && <CategoriesView />}
            {currentView.view === 'my tasks' && <MyTasksView />}
        </div>
    );
};

export default Projects;

